/**
 * Clicktale
 * @module
 */
var _ = require('optimizely-client-fn');
var di = require('core/di');
var EventEmitter = require('core/event_emitter');
var EventEmitterEnums = require('core/constants/event_emitter');
var Logger = require('core/utils/logger');
var Poll = require('core/utils/poll');
var WindowEnv = require('core/env/window');
var LayerDataStore = di.get('stores/layer_data');
var generateAnalyticsValueString = require('core/plugins/helpers/generate_analytics_value');

// Character limit as specified by Clicktale partner on 1/12/2017.
var CLICKTALE_MAX_STR_LENGTH = 255;

var CLICKTALE_INTEGRATION_EXPERIENCE_HANDLER = 'ClicktaleIntegrationExperienceHandler';

var VENDOR = 'clicktale';
var OPTIMIZELY_VENDOR_NAME = 'Optimizely';

/**
 * When plugin is registered, register a listener for action.applied events.
 */
function registerClicktale() {
  // We need to do this so that when ClicktaleIntegrationExperienceHandler exists on the Window,
  // we can redefine the push method to directly invoke it.
  var queue = [];

  var clicktaleQueue = {
    queue: queue,
    push: _.bind(queue.push, queue),
  };

  // Listen for all actions for a given layer decision to be complete, then notify Clicktale
  EventEmitter.on({
    filter: {
      type: EventEmitterEnums.TYPES.ACTION,
      name: 'appliedAllForDecision',
    },
    handler: _.partial(onAppliedAllForDecisionEmitted, clicktaleQueue),
  });
  pollForClicktale(clicktaleQueue);
}

module.exports = registerClicktale;

/**
 * Handler for an action.applied event.
 *
 * @param {Object} clicktaleQueue
 * @param {Object} event
 * @private
 */
function onAppliedAllForDecisionEmitted(clicktaleQueue:any, event:any) {
  var layer = LayerDataStore.get(event.data.decision.layerId);
  var settings = layer['integrationSettings'];

  if (!settings || _.isUndefined(settings[VENDOR])) {
    return;
  }

  var stringToSendToClicktale = buildClicktaleDataString(event.data.decision, layer);
  if (stringToSendToClicktale) {
    clicktaleQueue.push(stringToSendToClicktale);
    Logger.log('Pushing action.applied event to Clicktale queue. Data:', event);
  }
}

/**
 * Invoke the ClicktaleIntegrationExperienceHandler
 * @param {string} Analytics string
 * @param {string} Our vendor name. Required by Clicktale.
 * @private
 */
function invokeClicktaleIntegrationExperienceHandler(item) {
  WindowEnv.getGlobal(CLICKTALE_INTEGRATION_EXPERIENCE_HANDLER)(item, OPTIMIZELY_VENDOR_NAME);
}

/**
 * Poll for the ClicktaleIntegrationExperienceHandler on the Window. When it exists, then
 * process the queue. If not, add the data for Clicktale into the queue to process when the
 * handler is found.
 *
 * @param {Object} clicktaleQueue
 * @returns {Promise}
 * @private
 */
function pollForClicktale(clicktaleQueue:any): Promise<any> {
  var MAX_RETRIES = 50;
  var WAIT_TIME = __TEST__ ? 5 : 200;
  return Poll.pollFor(function isReadyToRecord() {
    return isClicktaleFnDefined();
  }, WAIT_TIME, MAX_RETRIES)
    .then(function processInitialQueue() {
      _.each(clicktaleQueue.queue, invokeClicktaleIntegrationExperienceHandler);
      // After processing the queue, we'll rewrite clicktaleQueue.push, which we defined
      // on plugin registration. From now onward, when clicktaleQueue.push is invoked, we
      // will invoke the Clicktale handler directly.
      clicktaleQueue.push = invokeClicktaleIntegrationExperienceHandler;
    });
}

/**
 * Build event data string to send to Clicktale.
 *
 * @param {Decision} decision
 * @param {Object} layer
 * @returns {string|undefined} Event data string to send to Clicktale or undefined.
 * @private
 */
function buildClicktaleDataString(decision:Decision, layer:any): string|undefined {
  var experiment = _.find(layer.experiments, {id: decision.experimentId});
  if (!experiment) {
    return;
  }

  var variation = _.find(experiment.variations, {id: decision.variationId});
  if (!variation) {
    return;
  }
  return generateAnalyticsValueString.generateAnalyticsString(
        layer, experiment, variation, decision.isLayerHoldback, CLICKTALE_MAX_STR_LENGTH, true);
}

/**
 * Return true or false based on whether the ClicktaleIntegrationExperienceHandler function
 * is on the Window object, so we can send event data to Clicktale.
 *
 * @returns {boolean}
 * @private
 */
function isClicktaleFnDefined(): boolean {
  var clicktaleIE = WindowEnv.getGlobal(CLICKTALE_INTEGRATION_EXPERIENCE_HANDLER);
  return _.isFunction(clicktaleIE);
}
